<script lang="ts">
	//Hooks
	import { onMount, onDestroy, createEventDispatcher } from 'svelte';
	//Formatters
	import { format } from 'svelte-i18n';
	//Design system components
	import { Container, IconButton, Input, Modal } from '@Shelly/shelly-design-system';
	//Helpers
	import { ScripsHelper } from '../scripts_helpers';
	import ShellyScriptEditor from '../Editor/ShellyScriptEditor';
	//Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Components
	import ScriptsSnippets from './ScriptsSnippets.svelte';
	import ScriptsConsole from './ScriptsConsole.svelte';
	//Types
	import type { Script } from '../../../../types/requests/settings/scripts';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { real_id } from 'src/helpers/interface_helpers';

	export let scripsHelper: ScripsHelper, script: Script;
	export let showEditScriptModal = false;

	let editor: ShellyScriptEditor,
		fullscreenBox: HTMLElement,
		waitForRunResponse = false,
		waitForSaveResponse = false,
		waitForScriptResponse = false,
		showSnippets = false,
		refreshBtn = false,
		scriptName = '',
		updatedCode = '',
		code = null,
		windowClientWidth: number;

	const dispatch = createEventDispatcher();
	const getScriptsCode = async () => {
		waitForScriptResponse = true;
		code = script.code ? script.code : await scripsHelper.getScriptsCode(script.id);
		if (code) {
			refreshBtn = false;
			updatedCode = code;
			editor.updateCode(code);
			editor.onUpdate((code: string) => {
				updatedCode = code;
			});
		} else if (!script.code) {
			refreshBtn = true;
		}
		waitForScriptResponse = false;
	};
	const initScriptCode = (container: HTMLElement) => {
		editor = new ShellyScriptEditor(container);
		getScriptsCode();
	};
	const runScript = async () => {
		if (waitForRunResponse) return;
		waitForRunResponse = true;
		await scripsHelper.runScript(script.id, running ? 'stop' : 'start');
		waitForRunResponse = false;
	};
	const saveScript = async () => {
		if (waitForSaveResponse) return;
		waitForSaveResponse = true;
		if (script.id == null) {
			script.id = await scripsHelper.createScripts(scriptName, updatedCode);
			if (script.id) {
				if (!scriptName) scriptName = `script_${script.id}`;
			}
		} else {
			if (scriptName !== scriptTitle) {
				if (!(await scripsHelper.updateScriptName(script.id, scriptName))) {
					scriptName = scriptTitle;
				}
			}
			const res = await scripsHelper.saveScriptsCode(script.id, updatedCode);
			if (res?.isok && running) {
				await scripsHelper.runScript(script.id, 'restart');
			}
		}
		waitForSaveResponse = false;
	};
	const insertScript = (e: CustomEvent) => {
		editor.insertScript(e.detail);
	};
	const handleKeydown = (e: KeyboardEvent) => {
		if (refreshBtn) refreshBtn = false;
		if (e.key == 'Escape') close();
		else if (e.ctrlKey && e.key == 's') {
			e.preventDefault();
			saveScript();
		}
	};
	const close = () => {
		dispatch('close');
	};
	$: settingsStore = getDeviceSettings(scripsHelper.deviceID);
	$: scriptTitle = script.id
		? $settingsStore?.[`script:${script.id}`]?.name || `script_${script.id}`
		: '';
	$: if (scriptTitle && !scriptName) {
		scriptName = scriptTitle;
	}

	onMount(() => {
		document.addEventListener('keydown', handleKeydown);
	});

	onDestroy(() => {
		document.removeEventListener('keydown', handleKeydown);
		if (editor) editor.destroy();
	});

	$: statusStore = getDeviceStatus(scripsHelper.deviceID);
	$: running = $statusStore?.[`script:${script.id}`]?.running;

	$: title = script?.edit
		? $format('modal_edit_script_heading', {
				default: 'Edit Script',
			})
		: $format('modal_create_new_script_heading', {
				default: 'Create New Script',
			});

	$: isMobile = windowClientWidth < SystemScreenBreakpoints.MOBILE_MAX;
</script>

<svelte:window bind:innerWidth={windowClientWidth} />

<Modal
	{title}
	bind:showModal={showEditScriptModal}
	type="fullScreen"
	on:close={() => {
		dispatch('close');
	}}
>
	<div slot="content" class="editor-container" bind:this={fullscreenBox}>
		<Container padding="0" class="mb-s">
			<div class="toolbar-content">
				<div class="script-name-wrapper">
					<Input
						size="m"
						type="text"
						icon="fas fa-brackets-curly"
						maxlength="50"
						label={$format('script_name_input_label', {
							default: 'Script name',
						})}
						bind:value={scriptName}
						id="input-r6nbbyb"
						aria-labelledby="input-r6nbbyb"
					/>
				</div>
				<div class="buttons-wrapper">
					{#if !isMobile}
						<IconButton
							icon="fa-solid fa-arrow-up-right-from-square"
							target="_blank"
							variant="secondary"
							href="https://shelly-api-docs.shelly.cloud/gen2/"
							on:click={ScripsHelper.openDocsLink}
						>
							{$format('shelly_apy_heading', { default: 'Shelly API' })}
						</IconButton>

						<IconButton
							icon="fas fa-code"
							variant="secondary"
							on:click={() => (showSnippets = true)}
						>
							{$format('snippets_button_heading', { default: 'Snippets' })}
						</IconButton>
					{/if}

					<IconButton
						icon="fas fa-trash"
						variant="danger"
						disabled={!script.id}
						on:click={() => {
							dispatch('delete', {
								scriptId: script.id,
							});
						}}
						aria-label={$format('button_delete', { default: 'Delete' })}
					/>

					<IconButton
						icon={`fas fa-${running ? 'pause' : 'play'}`}
						loading={waitForRunResponse}
						disabled={!script.id}
						on:click={runScript}
						aria-label={running ? $format('button_pause') : $format('button_play')}
					/>

					{#if refreshBtn}
						<IconButton
							icon="fas fa-refresh"
							loading={waitForScriptResponse}
							disabled={waitForScriptResponse}
							on:click={getScriptsCode}
							aria-label={$format('button_refresh', { default: 'Refresh' })}
						/>
					{:else}
						<IconButton
							icon="fas fa-save"
							loading={waitForSaveResponse}
							disabled={waitForScriptResponse || waitForSaveResponse}
							on:click={saveScript}
							aria-label={$format('button_save', { default: 'Save' })}
						/>
					{/if}
				</div>
			</div>
			{#if isMobile}
				<div class="d-flex mt-s gap-s width-full">
					<IconButton
						class="flex-1 min-width-0 width-auto text-nowrap"
						icon="fa-solid fa-arrow-up-right-from-square"
						target="_blank"
						variant="secondary"
						href="https://shelly-api-docs.shelly.cloud/gen2/"
						on:click={ScripsHelper.openDocsLink}
						aria-label={$format('shelly_apy_heading', {
							default: 'Shelly API',
						})}
					>
						{$format('shelly_apy_heading', { default: 'Shelly API' })}
					</IconButton>

					<IconButton
						class="flex-1 min-width-0 width-auto text-nowrap"
						icon="fas fa-code"
						variant="secondary"
						on:click={() => (showSnippets = true)}
						aria-label={$format('snippets_button_heading', {
							default: 'Snippets',
						})}
					>
						{$format('snippets_button_heading', { default: 'Snippets' })}
					</IconButton>
				</div>
			{/if}
		</Container>
		<div class="editor" use:initScriptCode>
			{#if waitForScriptResponse}
				<div class="loading">loading...</div>
			{/if}
			<ScriptsConsole {scripsHelper} scriptID={script.id} {running} />
		</div>
	</div>
</Modal>

{#if showSnippets}
	<ScriptsSnippets bind:showModal={showSnippets} on:insertScript={insertScript} />
{/if}

<style>
	.editor-container {
		display: flex;
		flex-direction: column;
		overflow: hidden;
		height: 100%;
		width: 100%;
		border-radius: 0;
	}

	.editor {
		position: relative;
		height: 100%;
		width: 100%;
		display: flex;
		flex-direction: column;
		border-radius: 8px;
		overflow: hidden;
	}

	.buttons-wrapper {
		display: flex;
		gap: 8px;
	}

	.script-name-wrapper {
		flex: 1;
		min-width: 168px;
	}

	.toolbar-content {
		display: flex;
		gap: 8px;
		padding: 4px;
	}

	.loading {
		font-size: 13px;
		font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
		padding-top: 10px;
		display: block;
		position: absolute;
		left: 50px;
		top: 2px;
		color: #4f559c;
		width: calc(100% - 50px);
		height: 100%;
		z-index: 5;
	}

	:global(.shelly-code-editor) {
		position: relative;
	}

	@media (max-width: 420px) {
		.script-name-wrapper {
			width: 100%;
			padding-bottom: 0;
		}
	}
</style>
